import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TicketcodeAuthService } from './_services/_auth/ticketcode-auth.service';
import { customApp } from './_configuration/configuration';
import { CustomAppService } from './_services/_content/customApp.service';
import { ShepherdService } from 'angular-shepherd';
import { TutorialService } from './_services/_tutorial/tutorial.service';
import { IColors, ICustomApp, IEventGeneralSettings, PushNotifyType } from '@sv/interfaces';
import { delay, takeUntil } from 'rxjs/operators';
import { WebsocketService } from './_services/_content/websocket.service';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PopupNotificationsDialogComponent } from './_shared/components/popup-notifications-dialog/popup-notifications-dialog.component';
import { CustomPushNotifyComponent } from './_shared/components/custom-push-notify/custom-push-notify.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  authenticated: boolean;
  app: ICustomApp;

  colors: IColors = {
    primaryColor: '#000000',
    secondaryColor: '#ffffff',
    fontColor: '#000000',
    headerBackgroundColor: '#ffffff',
    navBackgroundColor: '#ffffff',
    bottomNavBackgroundColor: 'blue',
    navLinkColor: '#ffffff',
    headlineColor: '#ffffff',
    toolbarButtonFontColor: '#ffffff',
    toolbarButtonColor: '#ffffff',
    loginButtonFontColor: '#000000',
    loginButtonBackgroundColor: '#ffffff',
    loginBackgroundColor: '#000000',
    moduleBackgroundColor: '#ffffff',
    subheadlineColor: '#000000',
    cardColor: '#ffffff',
    regHeadlineColor: '#000000'
  };
  loaded = false;
  booth = false;
  landingPage = false;

  destroyed$ = new Subject();

  constructor(
    private ticketcodeAuthService: TicketcodeAuthService,
    private customAppService: CustomAppService,
    private shepherdService: ShepherdService,
    private tutorialService: TutorialService,
    private webSocket: WebsocketService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}
  mobile = false;

  @HostListener('window:resize', [])
  onResize() {
    const width = window.innerWidth;
    this.mobile = width < 699;
  }

  ngOnInit(): void {
    this.mobile = document.documentElement.clientWidth < 699;
    this.customAppService.getBoothActive().subscribe(async (val) => {
      // otherwise expresion has changed error on dev
      await delay(0);
      this.booth = val;
    });
    this.ticketcodeAuthService.getAuthStatusListener().subscribe((auth) => {
      this.authenticated = auth;

      this.customAppService.getCustomAppListener().subscribe((app) => {
        this.app = app;
        const generalSettings: IEventGeneralSettings = app.generalSettings as IEventGeneralSettings;
        if (generalSettings.showOlark) {
          this.loadOlark();
        }

        this.landingPage = generalSettings.enableLP && location.pathname === '/';

        this.colors = customApp.colors as IColors;
        if (generalSettings.tutorial === undefined || generalSettings.tutorial === false) {
          sessionStorage.setItem('tutorial', 'false');
        } else {
          this.shepherdService.defaultStepOptions = this.tutorialService.getDefaultStepOptions();
          this.shepherdService.modal = true;
          this.shepherdService.confirmCancel = false;
          this.tutorialService.setTutorial(generalSettings.tutorialTour);
        }

        // All styles used in app.component.scss and root, body, dialogs on register/login must be added here
        document.documentElement.style.setProperty('--primary-color', this.colors.primaryColor);
        document.documentElement.style.setProperty('--headline-color', this.colors.headlineColor);
        document.documentElement.style.setProperty('--module-background-color', this.colors.moduleBackgroundColor);
        document.documentElement.style.setProperty('--font-color', this.colors.fontColor);
        document.documentElement.style.setProperty('--nav-link-color', this.colors.navLinkColor);
        document.documentElement.style.setProperty('--nav-background-color', this.colors.navBackgroundColor);
        document.documentElement.style.setProperty('--card-color', this.colors.cardColor);

        // to prevent navigation colors errors around border radius corners
        (document.styleSheets[3] as CSSStyleSheet).insertRule(`body { background-color: ${this.colors.moduleBackgroundColor} }`);
        if (this.colors.moduleBackgroundColor2) {
          (document.styleSheets[3] as CSSStyleSheet).insertRule(
            `body app-root { background: linear-gradient(to right, ${this.colors.moduleBackgroundColor}, ${this.colors.moduleBackgroundColor2}) }`
          );
        } else {
          (document.styleSheets[3] as CSSStyleSheet).insertRule(`body app-root { background-color: ${this.colors.moduleBackgroundColor} }`);
        }

        if (this.authenticated && generalSettings.enablePush) {
          this.webSocket
            .connect()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((res) => {
              if (res.type === PushNotifyType.PUSH) {
                if (this.mobile) {
                  this.snackBar.openFromComponent(CustomPushNotifyComponent, {
                    data: { description: res.description, buttonText: res.buttonText },
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                    panelClass: ['custom-snackbar']
                  });
                } else {
                  this.snackBar.openFromComponent(CustomPushNotifyComponent, {
                    data: { description: res.description, buttonText: res.buttonText },
                    panelClass: ['custom-snackbar']
                  });
                }
              } else if (res.type === PushNotifyType.POPUP) {
                if (this.mobile) {
                  this.dialog.open(PopupNotificationsDialogComponent, {
                    data: res,
                    width: '80%',
                    maxHeight: '70vh',
                    panelClass: 'customPanelPopup'
                  });
                } else {
                  this.dialog.open(PopupNotificationsDialogComponent, {
                    data: res,
                    width: '60%',
                    maxWidth: '1100px',
                    panelClass: 'customPanelPopup'
                  });
                }
              }
            });
        }

        this.loaded = true;
      });
    });
  }

  loadOlark() {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.innerText =
      ' ;(function(o,l,a,r,k,y){if(o.olark)return; r="script";y=l.createElement(r);r=l.getElementsByTagName(r)[0]; y.async=1;y.src="//"+a;r.parentNode.insertBefore(y,r); y=o.olark=function(){k.s.push(arguments);k.t.push(+new Date)}; y.extend=function(i,j){y("extend",i,j)}; y.identify=function(i){y("identify",k.i=i)}; y.configure=function(i,j){y("configure",i,j);k.c[i]=j}; k=y._={s:[],t:[+new Date],c:{},l:a}; })(window,document,"static.olark.com/jsclient/loader.js");olark.identify("8794-404-10-8512");';
    document.body.appendChild(script);
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
